import React from 'react';
import { Download, Laptop, Home, Heart, Move, User, Activity, Stethoscope, Zap } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { helpfulResources } from '../data/mockData';

const HelpfulResourcesPage = () => {
  const iconMap = {
    Laptop,
    Home,
    Heart,
    Move,
    User,
    Activity,
    Stethoscope,
    Zap
  };

  const handleDownload = (resource) => {
    // Open PDF in new tab for viewing/downloading
    window.open(resource.downloadLink, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-secondary to-background py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Stop Guessing. Start Understanding Your Body.
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
            Real life. Real bodies. Real solutions.
          </p>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Not generic advice—guides designed for how you actually live. Understand what your body is telling you, prevent avoidable pain, and make smarter choices to move, feel, and live better every day.
          </p>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {helpfulResources.map((resource) => {
              const Icon = iconMap[resource.icon];
              return (
                <Card key={resource.id} className="border-2 border-gray-100 hover:border-primary transition-all duration-300 hover:shadow-lg group">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary rounded-full group-hover:bg-primary transition-colors duration-300">
                        <Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors duration-300" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {resource.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {resource.description}
                    </p>
                    <Button 
                      onClick={() => handleDownload(resource)}
                      className="w-full bg-primary hover:bg-[hsl(var(--facebook-blue-hover))] text-white transition-all duration-300"
                      size="sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-none">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-primary mb-4">Important Note</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  These Home Treatment Plans are designed to provide general guidance and should not replace professional medical advice.
                </p>
                <p>
                  <strong>Before starting any exercise program:</strong>
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Consult with a healthcare professional if you have any existing medical conditions</li>
                  <li>Stop exercising if you experience increased pain, dizziness, or discomfort</li>
                  <li>Book a comprehensive assessment for personalized treatment recommendations</li>
                  <li>These exercises are supplementary and work best when combined with professional care</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Personalized Care?
          </h2>
          <p className="text-lg text-gray-100 mb-8">
            While these resources are helpful, nothing beats a personalized treatment plan designed specifically for your condition.
          </p>
          <Button 
            onClick={() => window.location.href = '/'}
            size="lg"
            className="bg-white text-primaryover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300"
          >
            Book Your Assessment
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HelpfulResourcesPage;
